'use client';

import React, { useState, useEffect } from 'react';
import { LayoutDashboard, BookmarkCheck, History, CalendarCheck, ListOrdered, User, TrendingUp, GraduationCap, ChevronRight, Plus, Trash2, Download, ArrowRight, LogOut, Lock, Eye, EyeOff, X, Clock, Video, Phone as PhoneIcon, MapPin, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import Link from 'next/link';
import { useAuth } from '@/context/AuthContext';
import { createClient } from '@/lib/supabase/client';

type TabId = 'overview' | 'saved' | 'history' | 'bookings' | 'choices' | 'profile';

const savedCollegesData = [
  { id: 'sv-001', name: 'Maulana Azad Medical College', shortName: 'MAMC Delhi', state: 'Delhi', type: 'Government', fees: '₹28,900/yr', cutoff: '300–350', probability: 'High' as const, savedOn: '22 Jun 2026' },
  { id: 'sv-002', name: 'VMMC & Safdarjung Hospital', shortName: 'VMMC Delhi', state: 'Delhi', type: 'Central', fees: '₹35,000/yr', cutoff: '450–680', probability: 'High' as const, savedOn: '22 Jun 2026' },
  { id: 'sv-003', name: 'Seth GS Medical College & KEM Hospital', shortName: 'KEM Mumbai', state: 'Maharashtra', type: 'Government', fees: '₹42,300/yr', cutoff: '850–1100', probability: 'Moderate' as const, savedOn: '21 Jun 2026' },
  { id: 'sv-004', name: 'Bangalore Medical College & Research Institute', shortName: 'BMCRI', state: 'Karnataka', type: 'Government', fees: '₹55,000/yr', cutoff: '1800–2500', probability: 'Low' as const, savedOn: '19 Jun 2026' },
];

const predictionHistoryData = [
  { id: 'ph-001', rank: 1200, category: 'General', quota: 'AIQ', date: '22 Jun 2026', matchCount: 127, highProb: 24 },
  { id: 'ph-002', rank: 1500, category: 'General', quota: 'AIQ', date: '18 Jun 2026', matchCount: 118, highProb: 18 },
  { id: 'ph-003', rank: 2000, category: 'General', quota: 'State', date: '14 Jun 2026', matchCount: 96, highProb: 12 },
];

const choiceListData = [
  { id: 'cl-001', rank: 1, name: 'Maulana Azad Medical College', shortName: 'MAMC Delhi', type: 'Government', cutoff: '300–350', priority: 'Must' as const },
  { id: 'cl-002', rank: 2, name: 'VMMC & Safdarjung Hospital', shortName: 'VMMC Delhi', type: 'Central', cutoff: '450–680', priority: 'Must' as const },
  { id: 'cl-003', rank: 3, name: 'Seth GS Medical College', shortName: 'KEM Mumbai', type: 'Government', cutoff: '850–1100', priority: 'Preferred' as const },
  { id: 'cl-004', rank: 4, name: 'Bangalore Medical College', shortName: 'BMCRI', type: 'Government', cutoff: '1800–2500', priority: 'Backup' as const },
];

const counsellors = [
  { id: 'c-001', name: 'Dr. Ananya Krishnan', specialty: 'AIQ Counselling Expert', experience: '12 years', available: ['10:00 AM', '11:00 AM', '2:00 PM', '3:00 PM', '4:00 PM'] },
  { id: 'c-002', name: 'Rajesh Patel', specialty: 'Maharashtra State Counselling', experience: '8 years', available: ['9:00 AM', '10:00 AM', '11:00 AM', '3:00 PM'] },
  { id: 'c-003', name: 'Dr. Meera Nair', specialty: 'Deemed University Admissions', experience: '10 years', available: ['10:00 AM', '12:00 PM', '2:00 PM', '5:00 PM'] },
  { id: 'c-004', name: 'Suresh Kumar', specialty: 'Karnataka & South India Quota', experience: '6 years', available: ['9:00 AM', '11:00 AM', '1:00 PM', '4:00 PM'] },
];

const tabs: { id: TabId; label: string; icon: React.ElementType; badge?: number }[] = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'saved', label: 'Saved Colleges', icon: BookmarkCheck, badge: savedCollegesData.length },
  { id: 'history', label: 'Predictions', icon: History, badge: predictionHistoryData.length },
  { id: 'bookings', label: 'Counselling', icon: CalendarCheck },
  { id: 'choices', label: 'Choice Filling', icon: ListOrdered },
  { id: 'profile', label: 'My Profile', icon: User },
];

const ProbBadge = ({ prob }: { prob: string }) => {
  if (prob === 'High') return <span className="prob-high px-2 py-0.5 rounded-full text-xs font-semibold">High</span>;
  if (prob === 'Moderate') return <span className="prob-moderate px-2 py-0.5 rounded-full text-xs font-semibold">Moderate</span>;
  return <span className="prob-low px-2 py-0.5 rounded-full text-xs font-semibold">Low</span>;
};

const StatusBadge = ({ status }: { status: string }) => {
  const map: Record<string, string> = {
    Confirmed: 'bg-success/10 text-success border border-success/20',
    Pending: 'bg-warning/10 text-warning border border-warning/20',
    Completed: 'bg-accent/10 text-accent border border-accent/20',
    Cancelled: 'bg-danger/10 text-danger border border-danger/20',
  };
  return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${map[status] || ''}`}>{status}</span>;
};

interface BookingFormData {
  counsellorId: string;
  counsellorName: string;
  specialty: string;
  date: string;
  time: string;
  mode: 'Video' | 'Phone' | 'In-person';
  topic: string;
}

function BookingModal({ onClose, onSuccess, userId }: { onClose: () => void; onSuccess: (booking: any) => void; userId?: string }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<BookingFormData>({
    counsellorId: '',
    counsellorName: '',
    specialty: '',
    date: '',
    time: '',
    mode: 'Video',
    topic: '',
  });
  const [loading, setLoading] = useState(false);

  const selectedCounsellor = counsellors.find(c => c.id === form.counsellorId);

  const getTomorrowDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  };

  const handleCounsellorSelect = (c: typeof counsellors[0]) => {
    setForm(prev => ({ ...prev, counsellorId: c.id, counsellorName: c.name, specialty: c.specialty, time: '' }));
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!form.date || !form.time || !form.topic.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      const supabase = createClient();
      const bookingData: any = {
        counsellor_name: form.counsellorName,
        specialty: form.specialty,
        booking_date: form.date,
        booking_time: form.time,
        mode: form.mode,
        topic: form.topic,
        booking_status: 'Pending',
      };
      if (userId) bookingData.student_id = userId;

      const { data, error } = await supabase.from('counselling_bookings').insert(bookingData).select().single();
      if (error) throw error;

      toast.success('Session booked successfully! We will confirm shortly.');
      onSuccess(data || { ...bookingData, id: `local-${Date.now()}`, status: 'Pending' });
    } catch {
      // Save locally if DB fails
      const localBooking = {
        id: `local-${Date.now()}`,
        counsellor: form.counsellorName,
        specialty: form.specialty,
        date: new Date(form.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
        time: form.time,
        mode: form.mode,
        status: 'Pending' as const,
        topic: form.topic,
      };
      toast.success('Session booked! Confirmation will be sent to your email.');
      onSuccess(localBooking);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-white/60 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-extrabold text-foreground">Book Counselling Session</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Step {step} of 3</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl text-muted-foreground hover:bg-slate-100 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Progress */}
        <div className="flex gap-1 px-6 pt-4">
          {[1, 2, 3].map(s => (
            <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${s <= step ? 'bg-primary' : 'bg-slate-100'}`} />
          ))}
        </div>

        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {/* Step 1: Choose Counsellor */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-foreground mb-3">Choose your counsellor</p>
              {counsellors.map(c => (
                <button
                  key={c.id}
                  onClick={() => handleCounsellorSelect(c)}
                  className="w-full flex items-start gap-3 p-4 rounded-2xl border border-slate-200 hover:border-primary/40 hover:bg-primary/3 transition-all text-left group"
                >
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {c.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-foreground">{c.name}</p>
                    <p className="text-xs text-muted-foreground">{c.specialty}</p>
                    <p className="text-xs text-primary mt-1">{c.experience} experience</p>
                  </div>
                  <ChevronRight size={16} className="text-muted-foreground group-hover:text-primary mt-1 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}

          {/* Step 2: Date & Time */}
          {step === 2 && selectedCounsellor && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-primary/5 rounded-2xl border border-primary/10 mb-4">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold text-sm">
                  {selectedCounsellor.name.charAt(0)}
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground">{selectedCounsellor.name}</p>
                  <p className="text-xs text-muted-foreground">{selectedCounsellor.specialty}</p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Select Date</label>
                <input
                  type="date"
                  min={getTomorrowDate()}
                  value={form.date}
                  onChange={e => setForm(prev => ({ ...prev, date: e.target.value }))}
                  className="input-glass text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Select Time</label>
                <div className="grid grid-cols-3 gap-2">
                  {selectedCounsellor.available.map(t => (
                    <button
                      key={t}
                      onClick={() => setForm(prev => ({ ...prev, time: t }))}
                      className={`py-2 px-3 rounded-xl text-xs font-semibold border transition-all ${form.time === t ? 'bg-primary text-white border-primary' : 'border-slate-200 text-foreground hover:border-primary/40'}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Session Mode</label>
                <div className="flex gap-2">
                  {(['Video', 'Phone', 'In-person'] as const).map(m => (
                    <button
                      key={m}
                      onClick={() => setForm(prev => ({ ...prev, mode: m }))}
                      className={`flex items-center gap-1.5 py-2 px-3 rounded-xl text-xs font-semibold border transition-all ${form.mode === m ? 'bg-primary text-white border-primary' : 'border-slate-200 text-foreground hover:border-primary/40'}`}
                    >
                      {m === 'Video' ? <Video size={12} /> : m === 'Phone' ? <PhoneIcon size={12} /> : <MapPin size={12} />}
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button onClick={() => setStep(1)} className="btn-secondary flex-1 py-2.5 text-sm">Back</button>
                <button
                  onClick={() => { if (form.date && form.time) setStep(3); else toast.error('Please select date and time'); }}
                  className="btn-primary flex-1 py-2.5 text-sm"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Topic & Confirm */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-2xl space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Counsellor</span><span className="font-semibold text-foreground">{form.counsellorName}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Date</span><span className="font-semibold text-foreground">{form.date ? new Date(form.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Time</span><span className="font-semibold text-foreground">{form.time}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Mode</span><span className="font-semibold text-foreground">{form.mode}</span></div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">What would you like to discuss? *</label>
                <textarea
                  value={form.topic}
                  onChange={e => setForm(prev => ({ ...prev, topic: e.target.value }))}
                  placeholder="e.g. AIQ Round 1 choice filling strategy, state quota options, deemed university fees..."
                  rows={3}
                  className="input-glass text-sm resize-none"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button onClick={() => setStep(2)} className="btn-secondary flex-1 py-2.5 text-sm">Back</button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2"
                >
                  {loading ? 'Booking...' : <><CheckCircle2 size={15} /> Confirm Booking</>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SignInGate({ onSignIn }: { onSignIn: (name: string) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { signIn, signUp } = useAuth();

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback?next=/student-dashboard`,
        },
      });
      if (error) throw error;
    } catch {
      toast.error('Google sign-in failed. Please try again.');
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Please fill in all fields'); return; }
    setLoading(true);
    try {
      if (isRegister) {
        await signUp(email, password, { fullName: name });
        toast.success('Account created! Welcome to Wikas Education.');
        onSignIn(name || email.split('@')[0]);
      } else {
        await signIn(email, password);
        toast.success('Welcome back!');
        onSignIn(email.split('@')[0]);
      }
    } catch {
      toast.error('Invalid credentials. Please check your email and password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="glass-card-white rounded-3xl p-8 border border-white/60 shadow-xl">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-6">
            <Lock size={28} className="text-white" />
          </div>
          <div className="text-center mb-8">
            <h2 className="text-2xl font-extrabold text-foreground mb-2">{isRegister ? 'Create Account' : 'Student Sign In'}</h2>
            <p className="text-muted-foreground text-sm">{isRegister ? 'Join Wikas Education to access your dashboard' : 'Sign in to access your personalized dashboard'}</p>
          </div>

          {/* Google Sign In */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={googleLoading || loading}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 transition-all font-semibold text-sm text-foreground mb-4 shadow-sm disabled:opacity-60"
          >
            {googleLoading ? (
              <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2045C17.64 8.5663 17.5827 7.9527 17.4764 7.3636H9V10.845H13.8436C13.635 11.97 13.0009 12.9231 12.0477 13.5613V15.8195H14.9564C16.6582 14.2527 17.64 11.9454 17.64 9.2045Z" fill="#4285F4"/>
                <path d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5613C11.2418 14.1013 10.2109 14.4204 9 14.4204C6.65591 14.4204 4.67182 12.8372 3.96409 10.71H0.957275V13.0418C2.43818 15.9831 5.48182 18 9 18Z" fill="#34A853"/>
                <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.5931 3.68182 9C3.68182 8.4069 3.78409 7.83 3.96409 7.29V4.9582H0.957275C0.347727 6.1731 0 7.5477 0 9C0 10.4523 0.347727 11.8269 0.957275 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
                <path d="M9 3.5795C10.3214 3.5795 11.5077 4.0336 12.4405 4.9255L15.0218 2.3441C13.4632 0.8918 11.4259 0 9 0C5.48182 0 2.43818 2.0168 0.957275 4.9582L3.96409 7.29C4.67182 5.1627 6.65591 3.5795 9 3.5795Z" fill="#EA4335"/>
              </svg>
            )}
            {googleLoading ? 'Redirecting...' : 'Continue with Google'}
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-muted-foreground font-medium">or</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Full Name</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Arjun Mehta" className="input-glass" />
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Email Address</label>
              <div className="relative">
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder=" " className="input-glass" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Password</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder=" " className="input-glass pr-10" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading || googleLoading} className="btn-primary w-full py-3.5 justify-center text-base">
              {loading ? 'Please wait...' : isRegister ? 'Create Account' : 'Sign In'}
            </button>
          </form>
          <p className="text-center text-sm text-muted-foreground mt-4">
            {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button onClick={() => setIsRegister(!isRegister)} className="text-primary font-semibold hover:underline">
              {isRegister ? 'Sign In' : 'Register Free'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default function StudentDashboardClient() {
  const { user, loading, signOut } = useAuth();
  const isAuthenticated = !!user;
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [choiceList, setChoiceList] = useState(choiceListData);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookings, setBookings] = useState<any[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);

  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Student';

  const handleSignIn = (_name: string) => {
    // Auth state is managed by Supabase — no local override needed
  };

  const handleSignOut = async () => {
    try { await signOut(); } catch { /* ignore */ }
    toast.success('Signed out successfully');
  };

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    const fetchBookings = async () => {
      setLoadingBookings(true);
      try {
        const supabase = createClient();
        const { data } = await supabase
          .from('counselling_bookings')
          .select('*')
          .eq('student_id', user.id)
          .order('created_at', { ascending: false });
        if (data && data.length > 0) setBookings(data);
      } catch { /* use local */ } finally {
        setLoadingBookings(false);
      }
    };
    fetchBookings();
  }, [isAuthenticated, user?.id]);

  const handleBookingSuccess = (booking: any) => {
    const normalized = {
      id: booking.id,
      counsellor: booking.counsellor || booking.counsellor_name,
      specialty: booking.specialty,
      date: booking.date || (booking.booking_date ? new Date(booking.booking_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''),
      time: booking.time || booking.booking_time,
      mode: booking.mode,
      status: booking.status || booking.booking_status || 'Pending',
      topic: booking.topic,
    };
    setBookings(prev => [normalized, ...prev]);
    setShowBookingModal(false);
    setActiveTab('bookings');
  };

  // Show loading spinner while auth state resolves
  if (loading) {
    return (
      <div className="pt-20 min-h-screen mesh-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="pt-20 min-h-screen mesh-bg">
        <SignInGate onSignIn={handleSignIn} />
      </div>
    );
  }

  const allBookings = bookings.length > 0 ? bookings : [
    { id: 'cb-001', counsellor: 'Dr. Ananya Krishnan', specialty: 'AIQ Counselling Expert', date: '26 Jun 2026', time: '11:00 AM', mode: 'Video', status: 'Confirmed', topic: 'AIQ Round 1 Choice Filling Strategy' },
    { id: 'cb-002', counsellor: 'Rajesh Patel', specialty: 'Maharashtra State Counselling', date: '24 Jun 2026', time: '3:00 PM', mode: 'Phone', status: 'Completed', topic: 'Maharashtra CAP Round 2 Strategy' },
  ];

  return (
    <div className="pt-20 min-h-screen mesh-bg">
      {showBookingModal && (
        <BookingModal
          onClose={() => setShowBookingModal(false)}
          onSuccess={handleBookingSuccess}
          userId={user?.id}
        />
      )}
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="glass-card-white rounded-3xl p-5 border border-white/60 sticky top-24">
              {/* Profile */}
              <div className="flex items-center gap-3 mb-5 pb-5 border-b border-slate-100">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {displayName.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-foreground truncate">{displayName}</p>
                  <p className="text-xs text-muted-foreground">NEET 2026 Aspirant</p>
                </div>
              </div>

              {/* Nav */}
              <nav className="space-y-1">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`sidebar-link w-full ${activeTab === tab.id ? 'active' : ''}`}
                  >
                    <tab.icon size={16} />
                    <span className="flex-1 text-left">{tab.label}</span>
                    {tab.badge !== undefined && tab.badge > 0 && (
                      <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">{tab.badge}</span>
                    )}
                  </button>
                ))}
              </nav>

              <div className="mt-5 pt-5 border-t border-slate-100">
                <button onClick={handleSignOut} className="sidebar-link w-full text-danger hover:bg-danger/5 hover:text-danger">
                  <LogOut size={16} />
                  Sign Out
                </button>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Overview */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <h1 className="text-2xl font-extrabold text-foreground">Welcome back, {displayName.split(' ')[0]}! 👋</h1>
                  <p className="text-muted-foreground text-sm mt-1">Here&apos;s your admission journey overview</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[
                    { label: 'Saved Colleges', value: savedCollegesData.length, icon: BookmarkCheck, color: 'text-primary', bg: 'bg-primary/8' },
                    { label: 'Predictions Run', value: predictionHistoryData.length, icon: TrendingUp, color: 'text-success', bg: 'bg-success/8' },
                    { label: 'Counselling Sessions', value: allBookings.length, icon: CalendarCheck, color: 'text-accent', bg: 'bg-accent/8' },
                    { label: 'Choice List', value: choiceList.length, icon: ListOrdered, color: 'text-warning', bg: 'bg-warning/8' },
                  ].map(stat => (
                    <div key={stat.label} className="glass-card-white rounded-2xl p-4 border border-white/60">
                      <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center mb-3`}>
                        <stat.icon size={18} className={stat.color} />
                      </div>
                      <p className="text-2xl font-extrabold text-foreground">{stat.value}</p>
                      <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
                    </div>
                  ))}
                </div>

                {/* Quick actions */}
                <div className="glass-card-white rounded-3xl p-6 border border-white/60">
                  <h2 className="text-base font-bold text-foreground mb-4">Quick Actions</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Link href="/neet-college-predictor" className="flex items-center gap-3 p-4 bg-primary/5 rounded-2xl border border-primary/10 hover:bg-primary/8 transition-colors group">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <GraduationCap size={18} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-foreground">Run New Prediction</p>
                        <p className="text-xs text-muted-foreground">Get updated college list</p>
                      </div>
                      <ChevronRight size={16} className="text-muted-foreground ml-auto group-hover:text-primary transition-colors" />
                    </Link>
                    <button onClick={() => setShowBookingModal(true)} className="flex items-center gap-3 p-4 bg-success/5 rounded-2xl border border-success/10 hover:bg-success/8 transition-colors group text-left">
                      <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center">
                        <CalendarCheck size={18} className="text-success" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-foreground">Book Counselling</p>
                        <p className="text-xs text-muted-foreground">Talk to an expert</p>
                      </div>
                      <ChevronRight size={16} className="text-muted-foreground ml-auto group-hover:text-success transition-colors" />
                    </button>
                  </div>
                </div>

                {/* Recent saved colleges */}
                <div className="glass-card-white rounded-3xl p-6 border border-white/60">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-base font-bold text-foreground">Recently Saved</h2>
                    <button onClick={() => setActiveTab('saved')} className="text-xs text-primary font-semibold hover:underline">View all</button>
                  </div>
                  <div className="space-y-3">
                    {savedCollegesData.slice(0, 3).map(c => (
                      <div key={c.id} className="flex items-center justify-between gap-3 p-3 bg-slate-50 rounded-xl">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-foreground truncate">{c.shortName}</p>
                          <p className="text-xs text-muted-foreground">{c.state} · {c.fees}</p>
                        </div>
                        <ProbBadge prob={c.probability} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Saved Colleges */}
            {activeTab === 'saved' && (
              <div className="space-y-4">
                <h1 className="text-2xl font-extrabold text-foreground">Saved Colleges</h1>
                <div className="space-y-3">
                  {savedCollegesData.map(c => (
                    <div key={c.id} className="glass-card-white rounded-2xl p-5 border border-white/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-bold text-foreground">{c.name}</p>
                        <p className="text-xs text-muted-foreground mt-1">{c.state} · {c.type} · {c.fees} · AIQ: {c.cutoff}</p>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <ProbBadge prob={c.probability} />
                        <Link href={`/college/${c.id}`} className="text-xs text-primary font-semibold hover:underline">View</Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Prediction History */}
            {activeTab === 'history' && (
              <div className="space-y-4">
                <h1 className="text-2xl font-extrabold text-foreground">Prediction History</h1>
                <div className="space-y-3">
                  {predictionHistoryData.map(p => (
                    <div key={p.id} className="glass-card-white rounded-2xl p-5 border border-white/60">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-sm font-bold text-foreground">NEET Rank {p.rank.toLocaleString('en-IN')} · {p.category} · {p.quota}</p>
                          <p className="text-xs text-muted-foreground mt-1">{p.date} · {p.matchCount} colleges matched</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-lg font-extrabold text-success">{p.highProb}</p>
                          <p className="text-xs text-muted-foreground">High probability</p>
                        </div>
                      </div>
                      <div className="mt-3">
                        <Link href="/neet-college-predictor" className="text-xs text-primary font-semibold hover:underline flex items-center gap-1">
                          Run again <ArrowRight size={11} />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Counselling Bookings */}
            {activeTab === 'bookings' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h1 className="text-2xl font-extrabold text-foreground">Counselling Sessions</h1>
                  <button onClick={() => setShowBookingModal(true)} className="btn-primary text-sm px-4 py-2.5 flex items-center gap-2">
                    <Plus size={15} />
                    Book Session
                  </button>
                </div>

                {/* Counsellors available */}
                <div className="glass-card-white rounded-3xl p-6 border border-white/60">
                  <h2 className="text-base font-bold text-foreground mb-4">Available Counsellors</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {counsellors.map(c => (
                      <div key={c.id} className="flex items-start gap-3 p-4 bg-slate-50 rounded-2xl">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                          {c.name.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-foreground">{c.name}</p>
                          <p className="text-xs text-muted-foreground">{c.specialty}</p>
                          <div className="flex items-center gap-1 mt-1.5">
                            <Clock size={11} className="text-primary" />
                            <span className="text-xs text-primary font-medium">{c.experience}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowBookingModal(true)}
                          className="text-xs text-primary font-semibold hover:underline flex-shrink-0"
                        >
                          Book
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* My bookings */}
                <div className="space-y-3">
                  <h2 className="text-base font-bold text-foreground">My Bookings</h2>
                  {loadingBookings ? (
                    <div className="glass-card-white rounded-2xl p-8 border border-white/60 text-center">
                      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">Loading your sessions...</p>
                    </div>
                  ) : allBookings.length === 0 ? (
                    <div className="glass-card-white rounded-2xl p-8 border border-white/60 text-center">
                      <CalendarCheck size={36} className="text-muted-foreground mx-auto mb-3 opacity-40" />
                      <p className="text-foreground font-semibold">No sessions booked yet</p>
                      <p className="text-sm text-muted-foreground mt-1">Book your first counselling session above</p>
                    </div>
                  ) : (
                    allBookings.map((b: any) => (
                      <div key={b.id} className="glass-card-white rounded-2xl p-5 border border-white/60">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm font-bold text-foreground">{b.counsellor || b.counsellor_name}</p>
                            <p className="text-xs text-muted-foreground">{b.specialty}</p>
                            <p className="text-xs text-muted-foreground mt-2">{b.date || b.booking_date} · {b.time || b.booking_time} · {b.mode}</p>
                            <p className="text-xs text-foreground mt-1 font-medium">{b.topic}</p>
                          </div>
                          <StatusBadge status={b.status || b.booking_status || 'Pending'} />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Choice Filling */}
            {activeTab === 'choices' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h1 className="text-2xl font-extrabold text-foreground">Choice Filling List</h1>
                  <button
                    onClick={() => toast.success('PDF export feature coming soon!')}
                    className="btn-secondary text-sm px-4 py-2.5 flex items-center gap-2"
                  >
                    <Download size={15} />
                    Export PDF
                  </button>
                </div>
                <div className="glass-card-white rounded-3xl border border-white/60 overflow-hidden">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>College</th>
                        <th>Type</th>
                        <th>Cutoff</th>
                        <th>Priority</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {choiceList.map(c => (
                        <tr key={c.id}>
                          <td className="font-bold text-primary">{c.rank}</td>
                          <td>
                            <p className="font-semibold text-foreground text-sm">{c.name}</p>
                            <p className="text-xs text-muted-foreground">{c.shortName}</p>
                          </td>
                          <td className="text-sm text-muted-foreground">{c.type}</td>
                          <td className="text-sm text-muted-foreground">{c.cutoff}</td>
                          <td>
                            <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${c.priority === 'Must' ? 'bg-success/10 text-success border border-success/20' : c.priority === 'Preferred' ? 'bg-primary/10 text-primary border border-primary/20' : 'bg-slate-100 text-muted-foreground'}`}>
                              {c.priority}
                            </span>
                          </td>
                          <td>
                            <button onClick={() => setChoiceList(prev => prev.filter(x => x.id !== c.id))} className="p-1.5 rounded-lg text-muted-foreground hover:text-danger hover:bg-danger/5 transition-colors">
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Profile */}
            {activeTab === 'profile' && (
              <div className="space-y-4">
                <h1 className="text-2xl font-extrabold text-foreground">My Profile</h1>
                <div className="glass-card-white rounded-3xl p-6 border border-white/60">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold text-xl">
                      {displayName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-lg font-extrabold text-foreground">{displayName}</p>
                      <p className="text-sm text-muted-foreground">NEET 2026 Aspirant</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { label: 'Full Name', value: displayName, placeholder: 'Enter your name' },
                      { label: 'Email', value: user?.email || 'student@example.com', placeholder: 'Enter email' },
                      { label: 'Mobile', value: '', placeholder: '+91 9876543210' },
                      { label: 'NEET Rank', value: '', placeholder: 'Enter your rank' },
                      { label: 'Category', value: 'General', placeholder: 'Select category' },
                      { label: 'Home State', value: '', placeholder: 'Select state' },
                    ].map(field => (
                      <div key={field.label}>
                        <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">{field.label}</label>
                        <input type="text" defaultValue={field.value} placeholder={field.placeholder} className="input-glass text-sm" />
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => toast.success('Profile saved successfully!')}
                    className="btn-primary mt-6 text-sm px-6 py-3"
                  >
                    Save Profile
                  </button>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}